import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  SD_849_REFERENCE_ORIGINS,
  SD_849_REFERENCE_STRATEGIES,
  ShipAndDebitEntityConfigSchema,
} from "@erp/shared-types";

/**
 * One S&D supplier as the suppliers admin page lists it.
 *
 * "S&D supplier" is deliberately the SAME set the 849 engine can resolve: an
 * `ap_suppliers` row whose company has a `ship-and-debit` entity_module_config
 * (that row's existence IS the module being enabled — there is no separate flag).
 * Listing anything wider would show suppliers the engine cannot route an email to;
 * anything narrower would hide one that is configured but idle.
 */
export const sdSupplierSummarySchema = z.object({
  supplierId: z.uuid(),
  companyId: z.uuid(),
  /** Legal name on the company record. */
  name: z.string(),
  /** `short_name` when set — what the 849 UI shows elsewhere. */
  displayName: z.string(),
  /** Every AS400 vendor number for the supplier (ABB has four). */
  vendorNumbers: z.array(z.string()),
  /** Trading-partner names/aliases — what resolves a parser config to this supplier. */
  tradingPartnerNames: z.array(z.string()),
  sd844DeliveryMethod: z.enum(["edi", "email"]).nullable(),
  /** Every channel this supplier answers 849s on — empty when none is declared.
   *  A list rather than a scalar because Mersen and ABB use both. */
  sd849ResponseMethods: z.array(z.enum(["edi", "email"])),
  /** Counts, so the list can show at a glance what is configured. */
  sd844EmailCount: z.int().nonnegative(),
  sd849EmailCount: z.int().nonnegative(),
  emailMatchingEnabled: z.boolean(),
  investigationEnabled: z.boolean(),
  /** Whether a parser config row exists for this supplier, and its status. */
  parserConfigStatus: z.enum(["active", "shadow", "draft"]).nullable(),
  /**
   * The `supplier_name` the matched parser config is actually STORED under, which is
   * frequently neither the company name nor the display name — configs are keyed by
   * their own formal name (`HOFFMAN` for "Hoffman", `ERICO CANADA INC.` for "Erico")
   * and are matched via `sd_trading_partner_names`. Any UI that loads or saves a
   * parser config must use THIS key: guessing the display name shows an empty editor
   * and saves a second config row that inbound processing never reads.
   */
  parserConfigSupplierName: z.string().nullable(),
  /**
   * How many DISTINCT config keys match this supplier. >1 is an ambiguous state:
   * which config reads an inbound email is decided by each config's own detection
   * patterns, so the one shown here may not be the one controlling live parsing —
   * the UI must surface that rather than present the picked key as authoritative.
   * (Multiple rows under ONE key are the supported multi-variant case, not this.)
   */
  parserConfigKeyCount: z.int().nonnegative(),
  /**
   * True when the supplier answers by email and has a parser config but matching is
   * off. That combination silently produces 849s with ZERO lines on the workflow
   * path, because the toggle gates parsing and not just matching — the single most
   * common misconfiguration in this module, so the list surfaces it directly.
   */
  needsMatchingToggle: z.boolean(),
});

export const listSdSuppliersResponseSchema = z.object({
  suppliers: z.array(sdSupplierSummarySchema),
});

const listContract = oc
  .input(z.object({}))
  .output(listSdSuppliersResponseSchema)
  .route({
    method: "GET",
    summary: "List ship and debit suppliers",
    description:
      "Lists every supplier with the ship and debit module enabled, with vendor numbers, response channels, parser config status, and flags for misconfigured email matching.",
  });

/** The full stored module config for one supplier, for the detail sections. */
export const getSdSupplierResponseSchema = z.object({
  supplier: sdSupplierSummarySchema,
  config: ShipAndDebitEntityConfigSchema,
});

const getContract = oc
  .input(z.object({ companyId: z.uuid() }))
  .output(getSdSupplierResponseSchema)
  .route({
    method: "GET",
    summary: "Get a ship and debit supplier's config",
    description:
      "Returns one supplier's ship and debit summary plus its full stored module configuration, looked up by company id.",
  });

/**
 * What the team's attachment-role labels say about where this supplier puts its
 * decision (#7169).
 *
 * Counts, not a verdict. It is evidence a human reads next to the parser config when
 * choosing which sources that config should look at — nothing here reaches a model,
 * by design: the label set moves with every label written, so anything tuned against
 * it could never be reproduced (#7159).
 */
export const attachmentRoleSourceEvidenceSchema = z.object({
  /** A lowercased file extension, `body` for the message itself, or `unknown`. */
  kind: z.string(),
  payload: z.int().nonnegative(),
  supporting: z.int().nonnegative(),
  irrelevant: z.int().nonnegative(),
  total: z.int().nonnegative(),
  /**
   * Share of this supplier's PAYLOAD labels, 0–1. Null when no labelled part of any
   * email carried the decision — which is not the same as "this source never does".
   */
  payloadShare: z.number().min(0).max(1).nullable(),
});

export const supplierLabelEvidenceResponseSchema = z.object({
  /** Label rows behind every number here. Always rendered: the samples are small. */
  sampleSize: z.int().nonnegative(),
  /** Distinct emails they came from — the smaller, honest count. */
  labelledEmails: z.int().nonnegative(),
  payloadCount: z.int().nonnegative(),
  bodyPayloadCount: z.int().nonnegative(),
  attachmentPayloadCount: z.int().nonnegative(),
  sources: z.array(attachmentRoleSourceEvidenceSchema),
});

const labelEvidenceContract = oc
  .input(z.object({ companyId: z.uuid() }))
  .output(supplierLabelEvidenceResponseSchema)
  .route({
    method: "GET",
    summary: "Get supplier attachment label evidence",
    description:
      "Counts how reviewers labelled a supplier's 849 email parts (body, file types) as payload, supporting, or irrelevant, showing where the supplier puts its claim decisions.",
  });

/**
 * One of the supplier's active 849 configurations with its standing in the
 * ranking (#7648): configurations are tried in `position` order until one
 * reads and places an email's rows. `score` adds the successes and subtracts
 * the failures, each decayed with age; `uses` counts every judged email.
 */
export const supplierConfigRankSchema = z.object({
  configId: z.uuid(),
  label: z.string(),
  /** Current version — every save appends one; none is ever deleted. */
  versionNumber: z.int().positive().nullable(),
  /** 1 = tried first. */
  position: z.int().positive(),
  score: z.number(),
  uses: z.int().nonnegative(),
  successes: z.int().nonnegative(),
  failures: z.int().nonnegative(),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
  createdAt: z.string(),
});

/**
 * One of the supplier's reference configurations (#7650) — where it writes our
 * VDM numbers — with its standing. Per place (subject, report rows) every
 * strategy that names a claim is a reference, in `position` order. `origin`:
 * held by every supplier, migrated from the old flags, or taught by a
 * reviewer's claim link.
 */
export const supplierReferenceRankSchema = z.object({
  strategy: z.enum(SD_849_REFERENCE_STRATEGIES),
  origin: z.enum(SD_849_REFERENCE_ORIGINS),
  position: z.int().positive(),
  score: z.number(),
  uses: z.int().nonnegative(),
  successes: z.int().nonnegative(),
  failures: z.int().nonnegative(),
  lastSuccessAt: z.string().nullable(),
  lastFailureAt: z.string().nullable(),
});

export const supplierConfigRankingResponseSchema = z.object({
  configurations: z.array(supplierConfigRankSchema),
  references: z.array(supplierReferenceRankSchema),
});

const configRankingContract = oc
  .input(z.object({ companyId: z.uuid() }))
  .output(supplierConfigRankingResponseSchema)
  .route({
    method: "GET",
    summary: "Get a supplier's 849 config ranking",
    description:
      "Lists a supplier's active 849 parsing configurations and claim-reference strategies in try order, with decayed scores, success and failure counts, and last-used dates.",
  });

/**
 * A NEW supplier's config is held to more than a stored one: the stored schema
 * keeps every field optional so legacy configs still parse, but setup must not
 * create a supplier whose 844s silently route as EDI (method left unset) or
 * cannot be sent (email delivery with no recipients).
 */
export const sdSupplierSetupConfigSchema =
  ShipAndDebitEntityConfigSchema.extend({
    sd_844_delivery_method: z.enum(["edi", "email"]),
  }).refine(
    (c) =>
      c.sd_844_delivery_method !== "email" ||
      (c.sd_844_email_addresses?.length ?? 0) > 0,
    {
      message: "Email delivery needs at least one 844 email address",
      path: ["sd_844_email_addresses"],
    },
  );

/**
 * Setup always creates a NEW company (with its AP supplier) — the server refuses
 * when a company with the same name or any of the vendor numbers already exists.
 */
export const setupSdSupplierInputSchema = z.object({
  companyName: z.string().trim().min(1).max(255),
  config: sdSupplierSetupConfigSchema,
});

export const setupSdSupplierResponseSchema = z.object({
  companyId: z.uuid(),
});

const setupContract = oc
  .input(setupSdSupplierInputSchema)
  .output(setupSdSupplierResponseSchema)
  .route({
    summary: "Set up a ship and debit supplier",
    description:
      "Creates a new company and AP supplier and sets it up for ship and debit with its trading partner names, 844 delivery, vendor numbers and 849 settings in one step. Refuses when a company with the same name or vendor number already exists, or when another supplier already claims a trading partner name.",
  });

export const check844sdFilesInputSchema = z
  .object({
    tradingPartner: z.string().trim().max(100).optional(),
    vendorNumber: z.string().trim().max(20).optional(),
  })
  .refine((v) => Boolean(v.tradingPartner || v.vendorNumber), {
    message: "Enter a trading partner or a vendor number",
  });

/** One (trading partner, vendor number) pair as it appears in the E844SD files. */
export const e844sdPartnerMatchSchema = z.object({
  tradingPartner: z.string().nullable(),
  vendorNumber: z.string().nullable(),
  /** Name in AS400's vendor master (VENDR1), when the vendor number is in it. */
  vendorName: z.string().nullable(),
  rowCount: z.int().nonnegative(),
  /** YYYYMMDD integers, as stored in the file. */
  firstInvoiceDate: z.int().nullable(),
  lastInvoiceDate: z.int().nullable(),
  lastImportedAt: z.string(),
  /** The S&D supplier whose trading-partner names already route these rows. */
  claimedBy: z.object({ companyId: z.uuid(), name: z.string() }).nullable(),
});

export const check844sdFilesResponseSchema = z.object({
  matches: z.array(e844sdPartnerMatchSchema),
  /** VENDR1 name for the searched vendor number, even when no rows exist yet. */
  vendorMasterName: z.string().nullable(),
});

const check844sdFilesContract = oc
  .input(check844sdFilesInputSchema)
  .output(check844sdFilesResponseSchema)
  .route({
    summary: "Check the 844SD files for a trading partner",
    description:
      "Searches the imported Rexel E844SD rows by trading partner name and/or vendor number, returning each trading partner and vendor pair with row counts, invoice date range, vendor master name, and the supplier already routing it.",
  });

export const sdSuppliersContracts = {
  list: listContract,
  get: getContract,
  labelEvidence: labelEvidenceContract,
  configRanking: configRankingContract,
  setup: setupContract,
  check844sdFiles: check844sdFilesContract,
} as const;

export type SetupSdSupplierInput = z.infer<typeof setupSdSupplierInputSchema>;
export type E844sdPartnerMatch = z.infer<typeof e844sdPartnerMatchSchema>;
export type Check844sdFilesResponse = z.infer<
  typeof check844sdFilesResponseSchema
>;

export type SupplierConfigRank = z.infer<typeof supplierConfigRankSchema>;
export type SupplierReferenceRank = z.infer<typeof supplierReferenceRankSchema>;
export type SupplierConfigRankingResponse = z.infer<
  typeof supplierConfigRankingResponseSchema
>;

export type SdSupplierSummary = z.infer<typeof sdSupplierSummarySchema>;
export type ListSdSuppliersResponse = z.infer<
  typeof listSdSuppliersResponseSchema
>;
export type GetSdSupplierResponse = z.infer<typeof getSdSupplierResponseSchema>;
export type SupplierLabelEvidenceResponse = z.infer<
  typeof supplierLabelEvidenceResponseSchema
>;
export type AttachmentRoleSourceEvidence = z.infer<
  typeof attachmentRoleSourceEvidenceSchema
>;
